import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  UserCircle, Clock, Umbrella, DollarSign, Heart, 
  GraduationCap, Target, Receipt, Bell, TrendingUp 
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function MyDashboardPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    attendance_this_month: 0,
    leave_balance: 0,
    pending_expenses: 0,
    upcoming_training: 0,
    my_goals_progress: 0,
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const res = await axios.get(`${API}/api/employee-portal/dashboard`);
      setStats(res.data);
    } catch (err) {
      // 403 = caller isn't a team_member — the page still renders with defaults,
      // so no need to show a red toast. Only surface real failures.
      if (err.response?.status !== 403) {
        console.error('Failed to fetch employee dashboard:', err);
        toast.error('Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
    }
  };

  const quickActions = [
    { icon: UserCircle, label: 'My Profile', path: '/employee/profile', color: 'bg-emerald-500' },
    { icon: Clock, label: 'Clock In/Out', path: '/employee/attendance', color: 'bg-blue-500' },
    { icon: Umbrella, label: 'Request Leave', path: '/employee/leave', color: 'bg-purple-500' },
    { icon: DollarSign, label: 'View Paystubs', path: '/employee/payroll', color: 'bg-green-500' },
    { icon: Heart, label: 'My Benefits', path: '/employee/benefits', color: 'bg-pink-500' },
    { icon: GraduationCap, label: 'Training Courses', path: '/employee/training', color: 'bg-indigo-500' },
    { icon: Target, label: 'My Goals', path: '/employee/goals', color: 'bg-orange-500' },
    { icon: Receipt, label: 'Submit Expense', path: '/employee/expenses', color: 'bg-yellow-500' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen p-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading your dashboard...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 bg-gray-50">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">My Dashboard</h1>
            <p className="text-gray-600 mt-1">Welcome back! Here's your workspace overview.</p>
          </div>
          <Button onClick={() => navigate('/employee/profile')} className="bg-emerald-500 hover:bg-emerald-600">
            <UserCircle className="h-4 w-4 mr-2" />
            View Profile
          </Button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Attendance This Month</CardTitle>
              <Clock className="h-4 w-4 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900">{stats.attendance_this_month} days</div>
              <p className="text-xs text-gray-500 mt-1">Working days logged</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Leave Balance</CardTitle>
              <Umbrella className="h-4 w-4 text-purple-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900">{stats.leave_balance} days</div>
              <p className="text-xs text-gray-500 mt-1">Available PTO</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Pending Expenses</CardTitle>
              <Receipt className="h-4 w-4 text-yellow-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900">{stats.pending_expenses}</div>
              <p className="text-xs text-gray-500 mt-1">Awaiting approval</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Goals Progress</CardTitle>
              <TrendingUp className="h-4 w-4 text-emerald-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900">{stats.my_goals_progress}%</div>
              <p className="text-xs text-gray-500 mt-1">30-60-90 day goals</p>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {quickActions.map((action, idx) => (
                <button
                  key={idx}
                  onClick={() => navigate(action.path)}
                  className="flex flex-col items-center gap-3 p-4 rounded-lg border border-gray-200 hover:border-emerald-500 hover:shadow-md transition-all group"
                >
                  <div className={`${action.color} p-3 rounded-lg group-hover:scale-110 transition-transform`}>
                    <action.icon className="h-6 w-6 text-white" />
                  </div>
                  <span className="text-sm font-medium text-gray-700 text-center">{action.label}</span>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Announcements */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Recent Announcements</CardTitle>
            <Bell className="h-5 w-5 text-gray-400" />
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-start gap-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="bg-blue-500 p-2 rounded-full">
                  <Bell className="h-4 w-4 text-white" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900">Welcome to the Employee Portal!</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    You can now manage your profile, attendance, leave requests, payroll, benefits, training, and more — all in one place.
                  </p>
                  <p className="text-xs text-gray-500 mt-2">Just now</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
